import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const termsSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `${siteUrl}/terms`,
  name: "Terms of Service — Hackers Lodge",
  description:
    "Hackers Lodge Terms of Service. Review the rules, obligations, and policies governing use of our cybersecurity talent marketplace and escrow platform.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        By accessing or using the Hackers Lodge website, platform, and services (collectively, the
        &ldquo;Services&rdquo;), you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do
        not agree to these Terms, you may not access or use the Services. These Terms apply to all users, including
        clients, hackers, visitors, and anyone else who accesses the platform.
      </p>
    ),
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          To use Hackers Lodge, you must:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>Be at least 18 years of age.</li>
          <li>Have the legal capacity to enter into a binding contract.</li>
          <li>Not be prohibited from using the Services under the laws of your jurisdiction.</li>
          <li>Provide accurate, complete, and current registration information.</li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          We reserve the right to suspend or terminate accounts that violate these eligibility requirements or
          provide false information.
        </p>
      </div>
    ),
  },
  {
    id: "accounts",
    title: "3. Account Responsibilities",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          You are responsible for maintaining the confidentiality of your account credentials and for all activities
          that occur under your account. You agree to:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>Use a strong, unique password and enable multi-factor authentication (MFA).</li>
          <li>Notify us immediately of any unauthorized access or security breach.</li>
          <li>Not share your account credentials with any third party.</li>
          <li>Not create more than one account without prior written permission.</li>
          <li>Not impersonate any person or entity or falsely claim an affiliation.</li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          You may not transfer or sell your account to another party. Hackers Lodge is not liable for any loss or
          damage arising from your failure to comply with these security obligations.
        </p>
      </div>
    ),
  },
  {
    id: "user-conduct",
    title: "4. Acceptable Use &amp; Prohibited Conduct",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          Hackers Lodge is a professional cybersecurity marketplace. You agree to use the platform only for lawful,
          legitimate, and ethical purposes. The following conduct is strictly prohibited:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>
            <strong className="text-gray-300">Illegal Activities:</strong> Using the platform to solicit, plan, or
            execute any illegal activity, including unauthorized access to systems, data theft, malware distribution,
            or any act that violates applicable laws.
          </li>
          <li>
            <strong className="text-gray-300">Harassment &amp; Abuse:</strong> Harassing, threatening, stalking, or
            abusing other users; posting hate speech, discriminatory content, or explicit material.
          </li>
          <li>
            <strong className="text-gray-300">Fraud &amp; Deception:</strong> Posting fake projects, submitting
            fraudulent credentials, misrepresenting skills or experience, or engaging in payment fraud.
          </li>
          <li>
            <strong className="text-gray-300">Platform Interference:</strong> Scraping, crawling, reverse
            engineering, or attempting to disrupt the platform&rsquo;s functionality through automated means or
            denial-of-service attacks.
          </li>
          <li>
            <strong className="text-gray-300">Circumvention:</strong> Attempting to bypass escrow, soliciting
            off-platform payments, or avoiding platform fees.
          </li>
          <li>
            <strong className="text-gray-300">Spam &amp; Solicitation:</strong> Sending unsolicited commercial
            messages, chain letters, or pyramid schemes through the platform.
          </li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          Violation of these provisions may result in immediate account suspension or termination, forfeiture of
          escrow funds, and referral to law enforcement authorities.
        </p>
      </div>
    ),
  },
  {
    id: "projects",
    title: "5. Projects &amp; Engagements",
    content: (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">5.1 Client Obligations</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Clients posting projects must provide accurate scope descriptions, realistic timelines, and sufficient
            detail for hackers to evaluate the engagement. Clients must fund escrow before work begins for
            escrow-protected projects. Failure to release escrow funds upon satisfactory delivery may result in
            dispute resolution proceedings.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">5.2 Hacker Obligations</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Hackers warrant that they possess the skills and credentials they claim. Hackers must deliver work that
            meets the agreed-upon scope, timeline, and quality standards. Hackers are responsible for maintaining
            the confidentiality of client data and systems they are granted access to during engagements.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">5.3 Scope of Authorization</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Hackers must operate strictly within the scope of authorization defined in each project. Any testing,
            access, or activity beyond the authorized scope constitutes unauthorized access and may result in account
            termination and legal liability. Clients are responsible for ensuring they have proper authorization for
            all systems included in the project scope.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">5.4 Disputes</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            In the event of a dispute between a client and a hacker, both parties agree to participate in good-faith
            resolution through Hackers Lodge&rsquo;s dispute resolution process. Hackers Lodge reserves the right
            to make binding determinations regarding escrow fund disbursement based on the evidence provided by
            both parties.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "payments",
    title: "6. Payments &amp; Escrow",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          All payments on Hackers Lodge are processed through our secure escrow system using cryptocurrency. By
          initiating a payment, you agree to the following:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>Funds are held in escrow until the project is completed and the client releases payment, or a dispute is resolved.</li>
          <li>Hackers Lodge charges a service fee on completed transactions, disclosed at the time of project creation.</li>
          <li>Cryptocurrency payments are irreversible once confirmed on the blockchain. Refunds are issued at Hackers Lodge&rsquo;s sole discretion.</li>
          <li>You are responsible for any network fees, gas costs, or exchange rate fluctuations associated with crypto transactions.</li>
          <li>Hackers Lodge is not responsible for losses due to incorrect wallet addresses provided by users.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "intellectual-property",
    title: "7. Intellectual Property",
    content: (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.1 Platform Content</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            The Hackers Lodge platform, including its design, code, branding, trademarks, and proprietary
            technology, is owned by Hackers Lodge and protected by intellectual property laws. You may not copy,
            modify, distribute, or create derivative works based on the platform without express written permission.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.2 User-Generated Content</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            By posting content (profile information, project descriptions, messages, deliverables) on the platform,
            you grant Hackers Lodge a non-exclusive, worldwide, royalty-free license to display, distribute, and
            reproduce that content solely for the purpose of operating and promoting the platform. You retain all
            ownership rights to your content.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.3 Project Deliverables</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Unless otherwise agreed in writing, intellectual property rights in project deliverables (reports, code,
            findings, recommendations) transfer to the client upon full payment. Hackers retain the right to
            reference anonymized engagement details in their portfolio unless the client requests confidentiality.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "confidentiality",
    title: "8. Confidentiality",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        Both clients and hackers agree to maintain the confidentiality of all non-public information exchanged during
        engagements, including system details, vulnerability findings, business data, trade secrets, and
        communications. This obligation survives termination of the engagement and these Terms. Hackers Lodge will
        not access or review private project communications except as necessary for dispute resolution, security
        investigations, or as required by law.
      </p>
    ),
  },
  {
    id: "termination",
    title: "9. Termination",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          Hackers Lodge may suspend or terminate your account at any time, with or without cause, including but not
          limited to:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>Violation of these Terms or any applicable laws.</li>
          <li>Fraudulent activity, including fake credentials or payment fraud.</li>
          <li>Extended account inactivity (12+ months without login).</li>
          <li>Requests from law enforcement or regulatory authorities.</li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          Upon termination, your right to access the Services ceases immediately. Any escrow funds held at the time
          of termination will be handled according to our dispute resolution process. Provisions that by their nature
          should survive termination (including confidentiality, intellectual property, limitation of liability, and
          dispute resolution) will survive.
        </p>
      </div>
    ),
  },
  {
    id: "disclaimer",
    title: "10. Disclaimers &amp; Limitation of Liability",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          <strong className="text-gray-300">THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo;</strong> without
          warranties of any kind, either express or implied. Hackers Lodge does not guarantee that:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>The platform will be uninterrupted, secure, or error-free.</li>
          <li>Hackers&rsquo; credentials, skills, or work quality are verified or guaranteed.</li>
          <li>Project outcomes will meet client expectations.</li>
          <li>Vulnerabilities found will be comprehensive or exhaustive.</li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          <strong className="text-gray-300">LIMITATION OF LIABILITY:</strong> To the fullest extent permitted by law,
          Hackers Lodge and its affiliates shall not be liable for any indirect, incidental, special, consequential,
          or punitive damages, including loss of profits, data, or business reputation, arising from your use of the
          Services. Our total liability for any claim shall not exceed the fees you paid to Hackers Lodge in the 12
          months preceding the claim.
        </p>
      </div>
    ),
  },
  {
    id: "indemnification",
    title: "11. Indemnification",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        You agree to indemnify, defend, and hold harmless Hackers Lodge, its officers, directors, employees, and
        agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable
        attorneys&rsquo; fees) arising from your use of the Services, your violation of these Terms, your
        infringement of any third-party rights, or your breach of applicable laws.
      </p>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to These Terms",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        We reserve the right to modify these Terms at any time. Material changes will be communicated via email
        (to the address associated with your account) and a prominent notice on our website at least 14 days before
        the changes take effect. Your continued use of the Services after the effective date constitutes acceptance
        of the revised Terms. If you do not agree to the changes, you must stop using the Services and may delete
        your account.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "13. Governing Law &amp; Dispute Resolution",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          These Terms are governed by the laws of the State of Delaware, United States, without regard to its
          conflict of law principles. Any dispute arising from these Terms or your use of the Services shall be
          resolved as follows:
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>
            <strong className="text-gray-300">Informal Resolution:</strong> Both parties agree to attempt to resolve
            the dispute informally by contacting the other party and negotiating in good faith for at least 30 days.
          </li>
          <li>
            <strong className="text-gray-300">Binding Arbitration:</strong> If informal resolution fails, disputes
            shall be resolved through binding arbitration administered by the American Arbitration Association (AAA)
            under its Commercial Arbitration Rules, held in Wilmington, Delaware.
          </li>
          <li>
            <strong className="text-gray-300">Class Action Waiver:</strong> All claims must be brought in an
            individual capacity. You waive the right to participate in a class action or class-wide arbitration.
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: "contact",
    title: "14. Contact Information",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          If you have questions about these Terms of Service, please contact us:
        </p>
        <div className="bg-[#161b22] rounded border border-gray-700 p-4 space-y-2">
          <p className="text-sm text-gray-300">
            <strong className="text-gray-200">Email:</strong>{" "}
            <a href="mailto:legal@hackerslodge.com" className="text-[#5bc0de] hover:underline">
              legal@hackerslodge.com
            </a>
          </p>
          <p className="text-sm text-gray-300">
            <strong className="text-gray-200">Support:</strong>{" "}
            <a href="mailto:support@hackerslodge.com" className="text-[#5bc0de] hover:underline">
              support@hackerslodge.com
            </a>
          </p>
          <p className="text-sm text-gray-300">
            <strong className="text-gray-200">Contact Form:</strong>{" "}
            <a href="/contact" className="text-[#5bc0de] hover:underline">
              hackerslodge.com/contact
            </a>
          </p>
        </div>
        <p className="text-xs text-gray-500">
          Last updated: July 23, 2026
        </p>
      </div>
    ),
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Terms of Service — Hackers Lodge Cybersecurity Marketplace Rules &amp; Guidelines"
        description="Review the Terms of Service for the Hackers Lodge cybersecurity marketplace. Covers account responsibilities, acceptable use, project engagements, payments, escrow, intellectual property, confidentiality, liability, and dispute resolution."
        keywords="terms of service, Hackers Lodge terms, cybersecurity marketplace terms, ethical hacking terms, hacker platform rules"
        canonicalUrl={`${siteUrl}/terms`}
        ogType="website"
        schema={termsSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-12 md:py-16 bg-[#161b22]">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "Home", to: "/" },
                { label: "Terms of Service" },
              ]}
            />

            <div className="mb-8 text-center">
              <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                Terms of Service
              </h1>
              <p className="text-sm text-gray-500">
                The rules and guidelines for using the Hackers Lodge platform.
              </p>
            </div>

            {/* Terms Sections */}
            <div className="bg-[#0d1117] rounded border border-gray-700 p-6 md:p-8 space-y-8">
              {sections.map((section) => (
                <article key={section.id} id={section.id} className="scroll-mt-24">
                  <h3 className="text-base font-semibold text-gray-200 mb-3">
                    {section.title}
                  </h3>
                  {section.content}
                  {section.id !== "contact" && (
                    <hr className="mt-6 border-gray-700/50" />
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}